<script setup lang="ts">
import { ref } from "vue";
import { IonContent, IonItem, IonList, IonLabel } from "@ionic/vue";
import { localLogger } from "@/utils/localLogger";
import { useI18n } from "@/composables/useI18n";
import { resetApplication } from "@/utils/resetApplication.js";
import { onMounted } from "vue";

const hasShared = ref<boolean>(false);
const hasUnlockedButton = ref<boolean>(false);

onMounted(async () => {
  await localLogger.log("[Diagnostic] - Modal opened");
});

const i18n = useI18n();

let clickCount = 0;
const CLICK_COUNT_TRIGGER = 5;

async function shareLogFile(): Promise<void> {
  try {
    await localLogger.downloadLogs();
    hasShared.value = true;
    await localLogger.log("[Diagnostic] - File shared");
  } catch {
    hasShared.value = false;
  }
}

async function onClick() {
  if (!hasShared.value) return;
  if (!hasUnlockedButton.value) return;
  hasUnlockedButton.value = false;
  await localLogger.log("[Diagnostic] - Resetting application");
  await resetApplication();
}

async function onHiddenTriggerClick() {
  if (!hasShared.value) return;
  if (clickCount === 0) {
    setTimeout(() => (clickCount = 0), 1_000);
  }
  clickCount += 1;
  if (clickCount >= CLICK_COUNT_TRIGGER) {
    clickCount = 0;
    hasUnlockedButton.value = true;
  }
}
</script>

<template>
  <ion-content class="ion-padding-top normal diagnostic-tool-modal">
    <ion-list lines="none" class="ion-margin-top">
      <ion-item>
        <div class="hint">
          {{ i18n.t("diagnostic.diagnosticToolsModalTitle") }}
        </div>
      </ion-item>
    </ion-list>
    <ion-list lines="none" class="diagnostic-actions-list">
      <ion-item class="clickable" @click="shareLogFile">
        <font-icon
          slot="start"
          name="send"
          material
          outlined
          size="1.2"
          color="var(--ion-color-primary)"
        />
        <ion-label class="list-item">{{
          i18n.t("diagnostic.diagnosticToolsShareLogFile")
        }}</ion-label>
      </ion-item>
      <ion-item
        :disabled="!hasShared || !hasUnlockedButton"
        class="clickable"
        @click="onClick"
      >
        <font-icon
          slot="start"
          name="heart_broken"
          material
          outlined
          size="1.2"
          :color="
            hasShared && hasUnlockedButton
              ? 'var(--ion-color-danger)'
              : 'var(--ion-color-primary)'
          "
        />
        <ion-label
          class="list-item"
          :color="
            hasShared && hasUnlockedButton
              ? 'danger'
              : 'var(--ion-color-primary)'
          "
        >
          {{ i18n.t("diagnostic.diagnosticToolsResetApplication") }}
        </ion-label>
      </ion-item>
      <ion-item
        class="clickable hidden-clickable-diagnostic-trigger"
        @click="onHiddenTriggerClick"
      />
    </ion-list>
  </ion-content>
</template>

<style scoped>
.hint {
  font-size: var(--font-size-m);
}
.diagnostic-actions-list {
  height: calc(100vh - 210px);
  overflow-y: auto;
}

.hidden-clickable-diagnostic-trigger {
  height: 200px;
  width: 100%;
}
</style>
